import { supabase } from '../lib/supabaseClient';
import { propertyMatchesFilters } from '../utils/propertyFilterEngine';

const toSnakeCase = (property) => ({
    property_id: property.propertyId || property.property_id || null,
    title: property.title,
    location: property.location || null,
    address: property.address || null,
    listing_date: property.listingDate || property.listing_date || null,
    property_type: property.propertyType || property.property_type || 'daire',
    description: property.description || null,
    gross_size: property.grossSize || property.gross_size || null,
    net_size: property.netSize || property.net_size || null,
    size: property.size || null,
    rooms: property.rooms != null ? String(property.rooms) : null,
    bathrooms: Number(property.bathrooms) || 1,
    building_age: property.buildingAge || property.building_age || null,
    floor: property.floor || null,
    total_floors: property.totalFloors || property.total_floors || null,
    heating: property.heating || null,
    kitchen: property.kitchen || null,
    balcony: property.balcony || null,
    elevator: property.elevator || null,
    parking: property.parking || null,
    furnished: property.furnished || null,
    usage_status: property.usageStatus || property.usage_status || null,
    in_site: property.inSite || property.in_site || null,
    site_name: property.siteName || property.site_name || null,
    dues: property.dues != null ? String(property.dues) : null,
    deposit: property.deposit != null ? String(property.deposit) : null,
    price: Number(String(property.price || 0).replace(/[^\d]/g, '')) || 0,
    deed_status: property.deedStatus || property.deed_status || null,
    from_who: property.fromWho || property.from_who || null,
    images: property.images || [],
    is_published: property.isPublished !== false
});

const fromSnakeCase = (row) => ({
    id: row.id,
    propertyId: row.property_id,
    title: row.title,
    location: row.location,
    address: row.address,
    listingDate: row.listing_date,
    propertyType: row.property_type,
    description: row.description,
    grossSize: row.gross_size,
    netSize: row.net_size,
    size: row.size,
    rooms: row.rooms,
    bathrooms: row.bathrooms,
    buildingAge: row.building_age,
    floor: row.floor,
    totalFloors: row.total_floors,
    heating: row.heating,
    kitchen: row.kitchen,
    balcony: row.balcony,
    elevator: row.elevator,
    parking: row.parking,
    furnished: row.furnished,
    usageStatus: row.usage_status,
    inSite: row.in_site,
    siteName: row.site_name,
    dues: row.dues,
    deposit: row.deposit,
    price: row.price,
    deedStatus: row.deed_status,
    fromWho: row.from_who,
    images: row.images || [],
    isPublished: row.is_published,
    createdAt: row.created_at,
    updatedAt: row.updated_at
});

export async function fetchProperties({ includeUnpublished = false } = {}) {
    if (!supabase) return [];

    let query = supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

    if (!includeUnpublished) {
        query = query.eq('is_published', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(fromSnakeCase);
}

export async function fetchPropertyById(id) {
    if (!supabase) return null;

    const numericId = Number(id);
    const queryId = Number.isFinite(numericId) ? numericId : id;

    const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('id', queryId)
        .single();

    if (error) return null;
    return fromSnakeCase(data);
}

export async function createProperty(property) {
    if (!supabase) throw new Error('Supabase yapılandırılmamış.');

    const payload = toSnakeCase(property);

    const { data, error } = await supabase
        .from('properties')
        .insert(payload)
        .select()
        .single();

    // Aynı ilan no ile tekrar import: güncelle
    if (error?.code === '23505' && payload.property_id) {
        const { data: updated, error: updateError } = await supabase
            .from('properties')
            .update(payload)
            .eq('property_id', payload.property_id)
            .select()
            .single();

        if (updateError) throw updateError;
        return fromSnakeCase(updated);
    }

    if (error) throw error;
    return fromSnakeCase(data);
}

export async function updatePropertyById(id, property) {
    if (!supabase) throw new Error('Supabase yapılandırılmamış.');

    const { data, error } = await supabase
        .from('properties')
        .update(toSnakeCase(property))
        .eq('id', id)
        .select()
        .single();

    if (error) throw error;
    return fromSnakeCase(data);
}

export async function deletePropertyById(id) {
    if (!supabase) throw new Error('Supabase yapılandırılmamış.');

    const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

    if (error) throw error;
    return true;
}

export async function searchPropertiesSupabase(filters = {}) {
    const properties = await fetchProperties({ includeUnpublished: true });
    return properties.filter((property) => propertyMatchesFilters(property, {
        searchTerm: filters.searchTerm,
        propertyType: filters.propertyType,
        roomCount: filters.roomCount,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minSize: filters.minSize,
        maxSize: filters.maxSize,
        buildingAge: filters.buildingAge,
        floor: filters.floor,
        heating: filters.heating,
        locationFilter: filters.location || filters.locationFilter
    }));
}

export { fromSnakeCase, toSnakeCase };
